import { useCallback, useEffect, useState } from 'react';
import { contextFactory } from '@frontend/design-system';
import { useLocationHistory } from '../hooks/use-location-history';
import { useCurrentLocationChildrenQuery } from '../queries/use-current-location-children-query';
import { useCurrentLocationQuery } from '../queries/use-current-location-query';
import { LocationServiceLocation } from '../types/location.types';

export type WamLocationServiceContext = {
  setCurrentLocationId: (locationId: string) => void;
  currentLocationId: string;
  currentLocation: LocationServiceLocation | null;
  currentLocationChildren: LocationServiceLocation[];
  isLoadingLocation: boolean;
  isLoadingChildren: boolean;
};

export const [LocationServiceContext, useLocationServiceContext] = contextFactory<WamLocationServiceContext>(
  'WamLocationServiceContext',
  {
    setCurrentLocationId: () => {},
    currentLocationId: '',
    currentLocation: null,
    currentLocationChildren: [],
    isLoadingLocation: false,
    isLoadingChildren: false,
  }
);

export const useLocationService = useLocationServiceContext;

export const LocationServiceProvider = ({ children }: { children: React.ReactNode }) => {
  const [currentLocationId, setCurrentLocationIdState] = useState<string>('');

  const { currentLocation, isLoadingLocation } = useCurrentLocationQuery(currentLocationId || '');
  const { childrenLocations, isLoadingChildren } = useCurrentLocationChildrenQuery(currentLocationId || '');

  const setCurrentLocationId = useCallback((locationId: string) => {
    setCurrentLocationIdState(locationId);
  }, []);

  const { locations: visitedLocations } = useLocationHistory();

  /**
   * Load the last visited location if no current location is set
   */
  useEffect(() => {
    if (!currentLocationId && visitedLocations.length) {
      setCurrentLocationId(visitedLocations[0].locationId);
    }
  }, [currentLocationId, visitedLocations]);

  return (
    <LocationServiceContext.Provider
      value={{
        currentLocationId,
        setCurrentLocationId,
        currentLocation: currentLocation || null,
        isLoadingLocation,
        currentLocationChildren: childrenLocations,
        isLoadingChildren,
      }}
    >
      {children}
    </LocationServiceContext.Provider>
  );
};
